import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import {
  PhoneInTalkOutlined,
  BadgeOutlined,
  TagOutlined,
  PsychologyOutlined,
  MemoryOutlined,
  SettingsPhoneOutlined,
  VpnKeyOutlined,
  CheckCircleOutlined,
  ContentCopyOutlined,
  TuneOutlined,
  PhoneForwardedOutlined,
  DeviceHubOutlined,
  DialpadOutlined,
  GraphicEqOutlined,
  TimerOutlined,
  QuestionAnswerOutlined,
  CallEndOutlined,
  InfoOutlined,
  ScheduleOutlined,
  LinkOutlined,
  SpeedOutlined,
  FiberManualRecordOutlined,
  RecordVoiceOverOutlined,
} from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";
import { useNavigate } from "react-router-dom";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import { REALTIME_MODELS } from "../../helpers/realtimeVoices";
import VoiceSelect from "../VoiceSelect";
import ConnectionModalSidebar from "../ConnectionModalSidebar";
import { isPromptOfType, promptOptionLabel, promptsOfType } from "../../helpers/promptTypes";

const SIP_GREEN = "#2fae60";

const useStyles = makeStyles()((theme) => ({
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  headerAvatar: {
    backgroundColor: theme.palette.mode === "light" ? "#e8f7ee" : "rgba(47,174,96,0.14)",
    color: SIP_GREEN,
  },
  cuerpo: {
    display: "flex",
    height: 460,
  },
  panel: {
    flex: 1,
    minWidth: 0,
    overflowY: "auto",
    padding: theme.spacing(2, 2.5),
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    paddingTop: theme.spacing(0.5),
  },
  row: {
    display: "flex",
    gap: theme.spacing(2),
    "& > *": {
      flex: 1,
      minWidth: 0,
    },
  },
  labelConTooltip: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  grupo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: theme.palette.text.disabled,
    marginTop: theme.spacing(1),
    "&::after": {
      content: '""',
      flex: 1,
      height: 1,
      background: theme.palette.divider,
    },
  },
  steps: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(0.5),
  },
  step: {
    display: "flex",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.mode === "light" ? "#faf9f7" : "rgba(255,255,255,0.02)",
  },
  stepIcon: {
    width: 30,
    height: 30,
    flexShrink: 0,
    backgroundColor: theme.palette.mode === "light" ? "#e8f7ee" : "rgba(47,174,96,0.14)",
    color: SIP_GREEN,
  },
  stepBodyCol: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    flex: 1,
    minWidth: 0,
  },
  stepTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  stepTitle: {
    fontWeight: 600,
    fontSize: "0.82rem",
  },
  stepBadge: {
    padding: theme.spacing(0.15, 0.75),
    borderRadius: 999,
    fontSize: "0.6rem",
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    color: theme.palette.text.disabled,
    background: theme.palette.mode === "light" ? "#ededea" : "rgba(255,255,255,0.06)",
    border: `1px solid ${theme.palette.divider}`,
    whiteSpace: "nowrap",
  },
  stepText: {
    fontSize: "0.76rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  code: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    borderRadius: 6,
    background: theme.palette.mode === "light" ? "#f0efec" : "#242320",
    border: `1px solid ${theme.palette.divider}`,
    fontFamily: "ui-monospace, Consolas, monospace",
    fontSize: "0.74rem",
    wordBreak: "break-all",
  },
  copyIcon: {
    fontSize: 15,
    cursor: "pointer",
    flexShrink: 0,
    color: theme.palette.text.secondary,
    "&:hover": { color: theme.palette.text.primary },
  },
}));

const FieldLabelTip = ({ classes, text, tip }) => (
    <span className={classes.labelConTooltip}>
        {text}
        <Tooltip title={tip}>
            <InfoOutlined fontSize="small" color="disabled" />
        </Tooltip>
    </span>
);

const SIP_WEEKDAYS = [
  { value: "mon", label: "Lunes" },
  { value: "tue", label: "Martes" },
  { value: "wed", label: "Miércoles" },
  { value: "thu", label: "Jueves" },
  { value: "fri", label: "Viernes" },
  { value: "sat", label: "Sábado" },
  { value: "sun", label: "Domingo" },
];

const SIP_WEEKDAY_LABELS = SIP_WEEKDAYS.reduce((acc, day) => {
  acc[day.value] = day.label;
  return acc;
}, {});

const genWebhookToken = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
};

const SipCallModal = ({ open, onClose, conexion, backendUrl }) => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [tab, setTab] = useState("config");

  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [promptId, setPromptId] = useState("");
  const [greetingMessage, setGreetingMessage] = useState("");
  const [transferNumber, setTransferNumber] = useState("");
  const [transferDtmfExtension, setTransferDtmfExtension] = useState("");
  const [asteriskExtension, setAsteriskExtension] = useState("");
  const [micSensitivity, setMicSensitivity] = useState("medium");
  const [maxDurationMinutes, setMaxDurationMinutes] = useState("10");
  const [silenceWarningSeconds, setSilenceWarningSeconds] = useState("20");
  const [silenceHangupSeconds, setSilenceHangupSeconds] = useState("15");
  const [dtmfCaptureEnabled, setDtmfCaptureEnabled] = useState(true);
  const [voiceSpeed, setVoiceSpeed] = useState("1");
  const [vadSilenceMs, setVadSilenceMs] = useState("500");
  const [noiseReduction, setNoiseReduction] = useState("near_field");
  const [transcriptionKeywords, setTranscriptionKeywords] = useState([]);
  const [recordingEnabled, setRecordingEnabled] = useState(true);
  const [recordingRetentionDays, setRecordingRetentionDays] = useState("15");
  const [callEndWebhookUrl, setCallEndWebhookUrl] = useState("");
  const [callEndWebhookMethod, setCallEndWebhookMethod] = useState("POST");
  const [callEndWebhookTimeoutSeconds, setCallEndWebhookTimeoutSeconds] = useState("8");
  const [transferHoursEnabled, setTransferHoursEnabled] = useState(false);
  const [transferHoursStart, setTransferHoursStart] = useState("09:00");
  const [transferHoursEnd, setTransferHoursEnd] = useState("18:00");
  const [transferHoursDays, setTransferHoursDays] = useState(["mon", "tue", "wed", "thu", "fri"]);
  const [realtimeModel, setRealtimeModel] = useState("gpt-realtime-2.1-mini");
  const [realtimeVoice, setRealtimeVoice] = useState("");
  const [prompts, setPrompts] = useState([]);
  const [saving, setSaving] = useState(false);

  const [webhookToken, setWebhookToken] = useState("");

  const [asteriskRoutes, setAsteriskRoutes] = useState([]);
  const [addingRoute, setAddingRoute] = useState(false);

  useEffect(() => {
    if (!open || tab !== "asterisk") return;

    (async () => {
      try {
        const { data } = await api.get("/asterisk/routes");
        setAsteriskRoutes(data.routes || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open, tab]);

  const routeNumeroLimpio = asteriskExtension.replace(/\D/g, "");
  const rutaYaAgregada = routeNumeroLimpio && asteriskRoutes.includes(routeNumeroLimpio);

  const agregarRutaAsterisk = async () => {
    if (!routeNumeroLimpio || addingRoute) return;
    setAddingRoute(true);

    try {
      const { data } = await api.put(`/asterisk/routes/${routeNumeroLimpio}`);
      setAsteriskRoutes(data.routes || []);
      if (data.reloadError) {
        toast.error(`Se agregó, pero el reload en vivo falló: ${data.reloadError}. Probá "Reiniciar servicio" en Configuración → Central telefónica.`);
      } else {
        toast.success(`Ruta ${routeNumeroLimpio} activa.`);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setAddingRoute(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    (async () => {
      try {
        const { data } = await api.get("/settings");
        const list = Array.isArray(data) ? data : [];
        const token = list.find((s) => s.key === "openaiSipWebhookToken")?.value;

        if (token) {
          setWebhookToken(token);
        } else {
          const nuevo = genWebhookToken();
          await api.put("/settings/openaiSipWebhookToken", { value: nuevo });
          setWebhookToken(nuevo);
        }
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    (async () => {
      try {
        const { data } = await api.get("/prompt");
        setPrompts(data?.prompts || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open || !conexion) return;

    setTab("config");
    setName(conexion.name || "");
    setNumber(conexion.number || "");
    setPromptId(conexion.promptId || "");
    setGreetingMessage(conexion.sipConfig?.greetingMessage || "");
    setTransferNumber(conexion.sipConfig?.transferNumber || "");
    setTransferDtmfExtension(conexion.sipConfig?.transferDtmfExtension || "");
    setAsteriskExtension(conexion.sipConfig?.asteriskExtension || conexion.number || "");
    setMicSensitivity(conexion.sipConfig?.micSensitivity || "medium");
    setMaxDurationMinutes(conexion.sipConfig?.maxDurationMinutes || "10");
    setSilenceWarningSeconds(conexion.sipConfig?.silenceWarningSeconds || "20");
    setSilenceHangupSeconds(conexion.sipConfig?.silenceHangupSeconds || "15");
    setDtmfCaptureEnabled(conexion.sipConfig?.dtmfCaptureEnabled !== false);
    setVoiceSpeed(String(conexion.sipConfig?.voiceSpeed || "1"));
    setVadSilenceMs(String(conexion.sipConfig?.vadSilenceMs || "500"));
    setNoiseReduction(conexion.sipConfig?.noiseReduction || "near_field");
    setTranscriptionKeywords(conexion.sipConfig?.transcriptionKeywords || []);
    setRecordingEnabled(conexion.sipConfig?.recordingEnabled !== false);
    setRecordingRetentionDays(String(conexion.sipConfig?.recordingRetentionDays || "15"));
    setCallEndWebhookUrl(conexion.sipConfig?.callEndWebhookUrl || "");
    setCallEndWebhookMethod(conexion.sipConfig?.callEndWebhookMethod || "POST");
    setCallEndWebhookTimeoutSeconds(String(conexion.sipConfig?.callEndWebhookTimeoutSeconds || "8"));
    setTransferHoursEnabled(Boolean(conexion.sipConfig?.transferHoursEnabled));
    setTransferHoursStart(conexion.sipConfig?.transferHoursStart || "09:00");
    setTransferHoursEnd(conexion.sipConfig?.transferHoursEnd || "18:00");
    setTransferHoursDays(
      conexion.sipConfig?.transferHoursDays?.length
        ? conexion.sipConfig.transferHoursDays
        : ["mon", "tue", "wed", "thu", "fri"]
    );
  }, [open, conexion]);

  const copiar = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success("Copiado.");
    } catch {}
  };

  const webhookUrl = `${backendUrl || ""}/sip-calls/webhook/${webhookToken || "..."}`;

  useEffect(() => {
    const chosen = prompts.find((p) => p.id === promptId);
    setRealtimeModel(chosen?.realtimeModel || "gpt-realtime-2.1-mini");
    setRealtimeVoice(chosen?.realtimeVoice || "");
  }, [promptId, prompts]);

  const handleSave = async () => {
    if (saving || !conexion?.id) return;
    setSaving(true);

    const failures = [];

    try {
      await api.put(`/whatsapp/${conexion.id}`, {
        name,
        number: number.trim(),
        promptId: promptId || null,
        sipConfig: {
          ...(conexion.sipConfig || {}),
          greetingMessage: greetingMessage.trim(),
          transferNumber: transferNumber.replace(/\D/g, ""),
          transferDtmfExtension: transferDtmfExtension.replace(/\D/g, ""),
          asteriskExtension: asteriskExtension.trim(),
          micSensitivity,
          maxDurationMinutes: maxDurationMinutes ? Number(maxDurationMinutes) : null,
          silenceWarningSeconds: silenceWarningSeconds ? Number(silenceWarningSeconds) : null,
          silenceHangupSeconds: silenceHangupSeconds ? Number(silenceHangupSeconds) : null,
          dtmfCaptureEnabled,
          voiceSpeed: Number(voiceSpeed),
          vadSilenceMs: Number(vadSilenceMs),
          noiseReduction,
          transcriptionKeywords,
          recordingEnabled,
          recordingRetentionDays: recordingRetentionDays ? Number(recordingRetentionDays) : 15,
          callEndWebhookUrl: callEndWebhookUrl.trim(),
          callEndWebhookMethod,
          callEndWebhookTimeoutSeconds: callEndWebhookTimeoutSeconds ? Number(callEndWebhookTimeoutSeconds) : 8,
          transferHoursEnabled,
          transferHoursStart,
          transferHoursEnd,
          transferHoursDays,
        },
      });
    } catch (err) {
      failures.push("el nombre/número/Prompt");
      toastError(err);
    }

    if (promptId) {
      const chosen = prompts.find((p) => p.id === promptId);

      if (chosen) {
        try {
          await api.put(`/prompt/${promptId}`, {
            ...chosen,
            realtimeModel,
            realtimeVoice,
          });
        } catch (err) {
          failures.push("el modelo/voz de Realtime");
          toastError(err);
        }
      }
    }

    setSaving(false);

    if (failures.length === 0) {
      toast.success("Conexión guardada");
      onClose();
    }
  };

  const badgeCuenta = (
    <span className={classes.stepBadge}>Una sola vez por cuenta</span>
  );

  const stepPrompt = (
    <div className={classes.step}>
      <Avatar className={classes.stepIcon} variant="rounded">
        <PsychologyOutlined style={{ fontSize: 17 }} />
      </Avatar>
      <div className={classes.stepBodyCol}>
        <div className={classes.stepTitleRow}>
          <Typography className={classes.stepTitle}>1. Prompt y voz</Typography>
        </div>
        <Typography className={classes.stepText}>
          En la pestaña <strong>Identidad</strong> elegí qué
          Prompt atiende este número y con qué voz habla. Sin voz elegida,
          OpenAI <strong>rechaza todas las llamadas</strong>, aunque el resto
          esté bien configurado.
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<TuneOutlined style={{ fontSize: 16 }} />}
          onClick={() => setTab("config")}
          style={{ alignSelf: "flex-start", marginTop: 4 }}
        >
          Ir a Identidad
        </Button>
      </div>
    </div>
  );

  const stepWebhook = (
    <div className={classes.step}>
      <Avatar className={classes.stepIcon} variant="rounded">
        <SettingsPhoneOutlined style={{ fontSize: 17 }} />
      </Avatar>
      <div className={classes.stepBodyCol}>
        <div className={classes.stepTitleRow}>
          <Typography className={classes.stepTitle}>2. Webhook en OpenAI</Typography>
          {badgeCuenta}
        </div>
        <Typography className={classes.stepText}>
          Entrá a <strong>platform.openai.com</strong> y andá a{" "}
          <em>Settings → Project → Webhooks</em>. Creá un webhook con esta
          URL y, como tipo de evento, elegí{" "}
          <strong>realtime.call.incoming</strong>.
        </Typography>
        <div className={classes.code}>
          <span style={{ flex: 1 }}>{webhookUrl}</span>
          <ContentCopyOutlined className={classes.copyIcon} onClick={() => copiar(webhookUrl)} />
        </div>
        <div className={classes.code}>
          <span style={{ flex: 1 }}>realtime.call.incoming</span>
          <ContentCopyOutlined className={classes.copyIcon} onClick={() => copiar("realtime.call.incoming")} />
        </div>
      </div>
    </div>
  );

  const stepSecret = (
    <div className={classes.step}>
      <Avatar className={classes.stepIcon} variant="rounded">
        <VpnKeyOutlined style={{ fontSize: 17 }} />
      </Avatar>
      <div className={classes.stepBodyCol}>
        <div className={classes.stepTitleRow}>
          <Typography className={classes.stepTitle}>3. Secreto del webhook</Typography>
          {badgeCuenta}
        </div>
        <Typography className={classes.stepText}>
          Al crear el webhook, OpenAI te muestra un secreto (empieza con{" "}
          <em>whsec_</em>). Copialo y pegalo en{" "}
          <strong>Configuración → OpenAI</strong>: sirve para comprobar que
          las llamadas que llegan son de verdad de OpenAI y no de otro.
        </Typography>
        <Typography className={classes.stepText}>
          En esa misma pantalla podés cargar, si querés, una{" "}
          <em>API key de rechazo</em>: la que se usa para colgar educadamente
          una llamada a un número que todavía no configuraste acá.
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<VpnKeyOutlined style={{ fontSize: 16 }} />}
          onClick={() => {
            onClose();
            navigate("/settings?tab=openai");
          }}
          style={{ alignSelf: "flex-start", marginTop: 4 }}
        >
          Ir a Configuración → OpenAI
        </Button>
      </div>
    </div>
  );

  const stepConfirm = (
    <div className={classes.step}>
      <Avatar className={classes.stepIcon} variant="rounded">
        <CheckCircleOutlined style={{ fontSize: 17 }} />
      </Avatar>
      <div className={classes.stepBodyCol}>
        <div className={classes.stepTitleRow}>
          <Typography className={classes.stepTitle}>5. Probar el número</Typography>
        </div>
        <Typography className={classes.stepText}>
          Guardá los cambios, llamá de verdad a{" "}
          <strong>{number || "(sin número todavía)"}</strong> desde un
          celular y confirmá que la IA atiende, antes de darlo por bueno.
        </Typography>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle style={{ paddingBottom: 4 }}>
        <div className={classes.header}>
          <Avatar className={classes.headerAvatar}>
            <PhoneInTalkOutlined />
          </Avatar>
          <div>
            <Typography style={{ fontWeight: 600, fontSize: "1rem", lineHeight: 1.3 }}>
              Llamada por teléfono
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Un número real, atendido por un Prompt (SIP + OpenAI Realtime)
            </Typography>
          </div>
        </div>
      </DialogTitle>

      <DialogContent dividers style={{ padding: 0 }}>
        <Box className={classes.cuerpo}>
          <ConnectionModalSidebar
            tabs={[
              { value: "config", label: "Identidad", icon: <TuneOutlined fontSize="small" /> },
              { value: "comportamiento", label: "Comportamiento", icon: <SettingsPhoneOutlined fontSize="small" /> },
              { value: "audio", label: "Audio", icon: <RecordVoiceOverOutlined fontSize="small" /> },
              { value: "transferencia", label: "Transferencia", icon: <PhoneForwardedOutlined fontSize="small" /> },
              { value: "grabacion", label: "Grabación", icon: <FiberManualRecordOutlined fontSize="small" /> },
              { value: "webhook", label: "Webhook", icon: <LinkOutlined fontSize="small" /> },
              { value: "asterisk", label: "Proveedor", icon: <DeviceHubOutlined fontSize="small" /> },
            ]}
            active={tab}
            onChange={setTab}
          />
          <div className={classes.panel}>
          {tab === "config" && (
            <div className={classes.form}>
              <TextField
                label="Nombre"
                size="small"
                variant="outlined"
                fullWidth
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeOutlined fontSize="small" color="disabled" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Solo para identificar esta conexión en el listado -- el cliente nunca lo ve ni lo escucha.">
                        <InfoOutlined fontSize="small" color="disabled" />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />

              <div className={classes.row}>
                <TextField
                  label="Número (DID)"
                  size="small"
                  variant="outlined"
                  placeholder="+593987654321"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TagOutlined fontSize="small" color="disabled" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="El que marca el cliente, en formato E.164. Si el sistema recibe la llamada con otro número (una extensión virtual, por ejemplo), no cambies este: poné ese otro en la pestaña Proveedor Telefónico, que el sistema reconoce cualquiera de los dos.">
                          <InfoOutlined fontSize="small" color="disabled" />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  select
                  label={
                    <FieldLabelTip classes={classes} text="Prompt (IA)" tip="Qué IA atiende esta línea. El resto de las pestañas (Comportamiento, Audio, Transferencia, etc.) se habilitan recién cuando elegís un Prompt acá." />
                  }
                  size="small"
                  variant="outlined"
                  fullWidth
                  value={promptId}
                  onChange={(e) => setPromptId(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PsychologyOutlined fontSize="small" color="disabled" />
                      </InputAdornment>
                    ),
                  }}
                >
                  <MenuItem value="">
                    <em>Sin asignar</em>
                  </MenuItem>
                  {promptsOfType(prompts, "voice", promptId).map((p) => (
                    <MenuItem key={p.id} value={p.id} disabled={!isPromptOfType(p, "voice")}>
                      {promptOptionLabel(p, "voice")}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              {promptId && (
                <>
                  <Typography className={classes.grupo}>Qué voz usa</Typography>

                  <TextField
                    select
                    label={
                      <FieldLabelTip classes={classes} text="Modelo (OpenAI Realtime)" tip="Qué modelo de OpenAI Realtime maneja la conversación de voz. El 'mini' es más barato y rápido; el completo entiende mejor casos complicados." />
                    }
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={realtimeModel}
                    onChange={(e) => setRealtimeModel(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MemoryOutlined fontSize="small" color="disabled" />
                        </InputAdornment>
                      ),
                    }}
                  >
                    {REALTIME_MODELS.map((m) => (
                      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                  </TextField>

                  <VoiceSelect
                    promptId={promptId}
                    value={realtimeVoice}
                    onChange={setRealtimeVoice}
                    error={!realtimeVoice}
                    helperText={!realtimeVoice ? "Sin voz elegida, OpenAI rechaza cualquier llamada a este número." : undefined}
                  />

                  <Typography className={classes.grupo}>Saludo</Typography>

                  <TextField
                    label={
                      <FieldLabelTip classes={classes} text="Mensaje de saludo (opcional)" tip="Lo que dice la IA apenas contesta. Vacío = la IA arma su propio saludo corto según el Prompt. Si lo escribís acá, lo dice tal cual, sin agregar nada antes ni después." />
                    }
                    size="small"
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Hola, soy Génesis de Expresito Carga. ¿En qué te puedo ayudar?"
                    value={greetingMessage}
                    onChange={(e) => setGreetingMessage(e.target.value)}
                  />
                </>
              )}
            </div>
          )}

          {tab === "comportamiento" && (
            <div className={classes.form}>
              {!promptId ? (
                <Typography variant="body2" color="textSecondary">
                  Elegí un Prompt en "Identidad" primero.
                </Typography>
              ) : (
                <>
                  <Typography className={classes.grupo}>Duración y micrófono</Typography>

                  <div className={classes.row}>
                    <TextField
                      select
                      label={
                        <FieldLabelTip classes={classes} text="Sensibilidad del micrófono" tip="Qué tan fuerte tiene que sonar la voz para que la IA la detecte. Alta capta voces bajas pero puede confundir ruido de fondo; baja ignora ruido pero exige hablar más fuerte." />
                      }
                      size="small"
                      variant="outlined"
                      value={micSensitivity}
                      onChange={(e) => setMicSensitivity(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <GraphicEqOutlined fontSize="small" color="disabled" />
                          </InputAdornment>
                        ),
                      }}
                    >
                      <MenuItem value="high">Alta</MenuItem>
                      <MenuItem value="medium">Media</MenuItem>
                      <MenuItem value="low">Baja</MenuItem>
                    </TextField>

                    <TextField
                      label="Duración máxima (minutos)"
                      size="small"
                      variant="outlined"
                      type="number"
                      value={maxDurationMinutes}
                      onChange={(e) => setMaxDurationMinutes(e.target.value.replace(/\D/g, ""))}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <TimerOutlined fontSize="small" color="disabled" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Si la llamada llega a este tiempo, se corta sola. Vacío = sin límite.">
                              <InfoOutlined fontSize="small" color="disabled" />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>

                  <Typography className={classes.grupo}>Teclado numérico</Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={dtmfCaptureEnabled}
                        onChange={(e) => setDtmfCaptureEnabled(e.target.checked)}
                      />
                    }
                    label={
                      <span className={classes.labelConTooltip}>
                        Si el cliente marca números por teclado, la IA los lee
                        <Tooltip title="Si te pregunta, por ejemplo, un número de cédula o de guía, el cliente lo puede marcar por teclado en vez de decirlo en voz alta, y la IA lo entiende igual.">
                          <InfoOutlined fontSize="small" color="disabled" />
                        </Tooltip>
                      </span>
                    }
                  />

                  <Typography className={classes.grupo}>Si deja de responder</Typography>

                  <div className={classes.row}>
                    <TextField
                      label="Preguntar tras (segundos)"
                      size="small"
                      variant="outlined"
                      type="number"
                      placeholder="Sin aviso"
                      value={silenceWarningSeconds}
                      onChange={(e) => setSilenceWarningSeconds(e.target.value.replace(/\D/g, ""))}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <QuestionAnswerOutlined fontSize="small" color="disabled" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Si el cliente se queda callado este tiempo, la IA le pregunta si sigue ahí.">
                              <InfoOutlined fontSize="small" color="disabled" />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      label="Colgar tras (segundos más)"
                      size="small"
                      variant="outlined"
                      type="number"
                      placeholder="Sin colgar"
                      value={silenceHangupSeconds}
                      onChange={(e) => setSilenceHangupSeconds(e.target.value.replace(/\D/g, ""))}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CallEndOutlined fontSize="small" color="disabled" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Si tampoco responde tras la pregunta tras este tiempo más, la IA cuelga sola.">
                              <InfoOutlined fontSize="small" color="disabled" />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "audio" && (
            <div className={classes.form}>
              {!promptId ? (
                <Typography variant="body2" color="textSecondary">
                  Elegí un Prompt en "Identidad" primero.
                </Typography>
              ) : (
                <>
                  <Typography className={classes.grupo}>Ritmo</Typography>

                  <TextField
                    select
                    label={
                      <FieldLabelTip classes={classes} text="Velocidad al hablar" tip="Qué tan rápido habla la IA. Normal es como habla una persona; Más rápida/lenta ajusta el ritmo sin cambiar el tono de voz." />
                    }
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={voiceSpeed}
                    onChange={(e) => setVoiceSpeed(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TimerOutlined fontSize="small" color="disabled" />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="0.75">Más lenta</MenuItem>
                    <MenuItem value="0.9">Lenta</MenuItem>
                    <MenuItem value="1">Normal</MenuItem>
                    <MenuItem value="1.1">Rápida</MenuItem>
                    <MenuItem value="1.25">Más rápida</MenuItem>
                  </TextField>

                  <Typography className={classes.grupo}>Ambiente</Typography>

                  <TextField
                    select
                    label={
                      <FieldLabelTip classes={classes} text="Reducción de ruido" tip="Cómo se limpia el audio que manda el micrófono del que llama antes de que la IA lo escuche. Usá 'Ambiente ruidoso' si suele llamar gente desde la calle, un taller, manos libres, etc." />
                    }
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={noiseReduction}
                    onChange={(e) => setNoiseReduction(e.target.value)}
                  >
                    <MenuItem value="near_field">Normal (teléfono cerca de la boca)</MenuItem>
                    <MenuItem value="far_field">Ambiente ruidoso (calle, taller, manos libres)</MenuItem>
                    <MenuItem value="none">Sin reducción</MenuItem>
                  </TextField>

                  <Autocomplete
                    multiple
                    freeSolo
                    autoSelect
                    options={[]}
                    value={transcriptionKeywords}
                    onChange={(e, values) =>
                      setTranscriptionKeywords(
                        Array.from(
                          new Set(values.flatMap((value) => String(value).split(",")).map((value) => value.trim()).filter(Boolean))
                        )
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          <FieldLabelTip classes={classes} text="Palabras clave (transcripción)" tip="Nombres propios y términos que se dicen en las llamadas: marca, oficinas, ciudades, códigos de guía. Escribe una y presiona Enter. Ayudan a que la transcripción del historial los escriba bien." />
                        }
                        size="small"
                        variant="outlined"
                        placeholder={transcriptionKeywords.length ? "" : "Ej.: Corona y presiona Enter"}
                      />
                    )}
                  />

                  <Typography className={classes.grupo}>Reacción al hablar</Typography>

                  <TextField
                    select
                    label={
                      <FieldLabelTip classes={classes} text="Qué tan rápido responde" tip="Cuánto silencio espera la IA antes de asumir que terminaste de hablar. Muy rápida responde antes pero puede cortarte a mitad de frase; Muy paciente espera más pero se siente más lenta." />
                    }
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={vadSilenceMs}
                    onChange={(e) => setVadSilenceMs(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SpeedOutlined fontSize="small" color="disabled" />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="300">Muy rápida (puede cortar a mitad de frase)</MenuItem>
                    <MenuItem value="500">Normal</MenuItem>
                    <MenuItem value="800">Paciente</MenuItem>
                    <MenuItem value="1200">Muy paciente</MenuItem>
                  </TextField>
                </>
              )}
            </div>
          )}

          {tab === "transferencia" && (
            <div className={classes.form}>
              {!promptId ? (
                <Typography variant="body2" color="textSecondary">
                  Elegí un Prompt en "Identidad" primero.
                </Typography>
              ) : (
                <>
                  <Typography className={classes.grupo}>Transferencia a un humano</Typography>

                  <div className={classes.row}>
                    <TextField
                      label="Número de transferencia"
                      size="small"
                      variant="outlined"
                      placeholder="+593987654321"
                      value={transferNumber}
                      onChange={(e) => setTransferNumber(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneForwardedOutlined fontSize="small" color="disabled" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="A qué número se deriva la llamada cuando la IA decide transferir, o cuando quien llama marca #. Vacío = sin transferencia disponible en este número.">
                              <InfoOutlined fontSize="small" color="disabled" />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      label="Extensión del IVR (opcional)"
                      size="small"
                      variant="outlined"
                      placeholder="1999"
                      value={transferDtmfExtension}
                      onChange={(e) => setTransferDtmfExtension(e.target.value.replace(/\D/g, ""))}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <DialpadOutlined fontSize="small" color="disabled" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Solo si el número de arriba contesta con un IVR/recepcionista antes de llegar a la persona: la extensión que hay que marcar por tonos apenas contesta. Vacío = se transfiere directo, sin marcar nada después.">
                              <InfoOutlined fontSize="small" color="disabled" />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>

                  <Typography className={classes.grupo}>Horario de los asesores</Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={transferHoursEnabled}
                        onChange={(e) => setTransferHoursEnabled(e.target.checked)}
                      />
                    }
                    label={
                      <span className={classes.labelConTooltip}>
                        Limitar el horario en que hay asesores disponibles
                        <Tooltip title="La IA sigue contestando llamadas las 24 horas. Esto solo limita cuándo puede transferir a un humano -- fuera de este horario, le avisa al cliente que no hay nadie disponible y sigue ayudándolo ella misma.">
                          <InfoOutlined fontSize="small" color="disabled" />
                        </Tooltip>
                      </span>
                    }
                  />

                  {transferHoursEnabled && (
                    <>
                      <div className={classes.row}>
                        <TextField
                          label="Desde"
                          size="small"
                          variant="outlined"
                          type="time"
                          value={transferHoursStart}
                          onChange={(e) => setTransferHoursStart(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <ScheduleOutlined fontSize="small" color="disabled" />
                              </InputAdornment>
                            ),
                          }}
                        />

                        <TextField
                          label="Hasta"
                          size="small"
                          variant="outlined"
                          type="time"
                          value={transferHoursEnd}
                          onChange={(e) => setTransferHoursEnd(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <ScheduleOutlined fontSize="small" color="disabled" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </div>

                      <FormControl size="small" variant="outlined" fullWidth>
                        <InputLabel id="transfer-hours-days-label">Días</InputLabel>
                        <Select
                          labelId="transfer-hours-days-label"
                          label="Días"
                          multiple
                          value={transferHoursDays}
                          onChange={(e) => setTransferHoursDays(e.target.value)}
                          renderValue={(selected) =>
                            selected
                              .map((code) => SIP_WEEKDAY_LABELS[code] || code)
                              .join(", ")
                          }
                        >
                          {SIP_WEEKDAYS.map((day) => (
                            <MenuItem key={day.value} value={day.value}>
                              <Checkbox size="small" checked={transferHoursDays.includes(day.value)} />
                              <ListItemText primary={day.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {tab === "grabacion" && (
            <div className={classes.form}>
              {!promptId ? (
                <Typography variant="body2" color="textSecondary">
                  Elegí un Prompt en "Identidad" primero.
                </Typography>
              ) : (
                <>
                  <Typography className={classes.grupo}>Grabación</Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={recordingEnabled}
                        onChange={(e) => setRecordingEnabled(e.target.checked)}
                      />
                    }
                    label={
                      <span className={classes.labelConTooltip}>
                        Guardar la grabación de las llamadas
                        <Tooltip title="Si lo apagás, Asterisk igual sube la grabación por un instante, pero el sistema la borra al toque y nunca queda ligada a la llamada ni accesible desde acá.">
                          <InfoOutlined fontSize="small" color="disabled" />
                        </Tooltip>
                      </span>
                    }
                  />

                  {recordingEnabled && (
                    <TextField
                      select
                      label={
                        <FieldLabelTip classes={classes} text="Borrar automáticamente después de" tip="Una tarea corre una vez al día y borra las grabaciones más viejas que este límite -- el archivo de audio y el link quedan eliminados, no solo ocultos." />
                      }
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={recordingRetentionDays}
                      onChange={(e) => setRecordingRetentionDays(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScheduleOutlined fontSize="small" color="disabled" />
                          </InputAdornment>
                        ),
                      }}
                    >
                      <MenuItem value="5">5 días</MenuItem>
                      <MenuItem value="10">10 días</MenuItem>
                      <MenuItem value="15">15 días</MenuItem>
                      <MenuItem value="20">20 días</MenuItem>
                      <MenuItem value="30">30 días</MenuItem>
                      <MenuItem value="60">60 días</MenuItem>
                    </TextField>
                  )}
                </>
              )}
            </div>
          )}

          {tab === "webhook" && (
            <div className={classes.form}>
              {!promptId ? (
                <Typography variant="body2" color="textSecondary">
                  Elegí un Prompt en "Identidad" primero.
                </Typography>
              ) : (
                <>
                  <Typography className={classes.grupo}>Webhook</Typography>

                  <TextField
                    label="URL (opcional, al terminar la llamada)"
                    size="small"
                    variant="outlined"
                    fullWidth
                    placeholder="https://tu-sistema.com/webhook"
                    value={callEndWebhookUrl}
                    onChange={(e) => setCallEndWebhookUrl(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkOutlined fontSize="small" color="disabled" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Apenas cuelga la llamada, el sistema manda a esta URL un resumen: número del que llamó, resultado, duración y la transcripción completa. Vacío = no manda nada.">
                            <InfoOutlined fontSize="small" color="disabled" />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <div className={classes.row}>
                    <TextField
                      select
                      label={
                        <FieldLabelTip classes={classes} text="Método" tip="Con qué método HTTP se manda el request a tu URL." />
                      }
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={callEndWebhookMethod}
                      onChange={(e) => setCallEndWebhookMethod(e.target.value)}
                    >
                      <MenuItem value="POST">POST</MenuItem>
                      <MenuItem value="GET">GET</MenuItem>
                      <MenuItem value="PUT">PUT</MenuItem>
                      <MenuItem value="PATCH">PATCH</MenuItem>
                    </TextField>

                    <TextField
                      label="Timeout (segundos)"
                      size="small"
                      variant="outlined"
                      type="number"
                      value={callEndWebhookTimeoutSeconds}
                      onChange={(e) => setCallEndWebhookTimeoutSeconds(e.target.value.replace(/\D/g, ""))}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Cuánto espera el sistema una respuesta de tu URL antes de darla por perdida. No afecta la llamada -- esto corre después de que ya colgó.">
                              <InfoOutlined fontSize="small" color="disabled" />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "asterisk" && (
            <div className={classes.steps}>
              {stepPrompt}
              {stepWebhook}
              {stepSecret}

              <div className={classes.step}>
                <Avatar className={classes.stepIcon} variant="rounded">
                  <DeviceHubOutlined style={{ fontSize: 17 }} />
                </Avatar>
                <div className={classes.stepBodyCol}>
                  <div className={classes.stepTitleRow}>
                    <Typography className={classes.stepTitle}>4. Ruta hacia la IA</Typography>
                  </div>
                  <Typography className={classes.stepText}>
                    Para cuando tu central telefónica no deja crear el
                    troncal directo. El sistema hace de puente: recibe la
                    llamada por el troncal que ya tenés y la reenvía a
                    OpenAI. Antes había que configurar esto a mano por SSH --
                    ahora este botón agrega la ruta solo.
                  </Typography>

                  <TextField
                    label="Extensión de la ruta"
                    size="small"
                    variant="outlined"
                    fullWidth
                    placeholder="59346020884"
                    helperText="La que usa el sistema para enrutar la llamada -- no siempre es igual al número (DID) de arriba, por ejemplo si la central reenvía la llamada por una extensión virtual antes de que llegue acá. Se precarga con el DID, pero la podés cambiar."
                    value={asteriskExtension}
                    onChange={(e) => setAsteriskExtension(e.target.value.replace(/\D/g, ""))}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DialpadOutlined fontSize="small" color="disabled" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {!routeNumeroLimpio ? (
                    <Typography className={classes.stepText} style={{ fontStyle: "italic" }}>
                      Completá la extensión de arriba primero.
                    </Typography>
                  ) : rutaYaAgregada ? (
                    <div className={classes.code} style={{ color: SIP_GREEN }}>
                      <CheckCircleOutlined style={{ fontSize: 16 }} />
                      <span style={{ flex: 1 }}>La ruta {routeNumeroLimpio} ya está activa.</span>
                    </div>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      style={{ alignSelf: "flex-start", backgroundColor: SIP_GREEN }}
                      startIcon={<DeviceHubOutlined fontSize="small" />}
                      disabled={addingRoute}
                      onClick={agregarRutaAsterisk}
                    >
                      {addingRoute ? "Agregando…" : "Activar este número"}
                    </Button>
                  )}

                  <Typography className={classes.stepText}>
                    Ni en tu central telefónica ni acá hace falta crear
                    troncal ni puerto nuevo: <strong>todas</strong> las
                    líneas comparten el mismo puente hacia OpenAI, y se
                    diferencian solo por la extensión de arriba. En tu
                    central, la ruta de ese número tiene que ir al mismo
                    troncal que ya usás para esto, no a una extensión ni al
                    recepcionista digital.
                  </Typography>
                </div>
              </div>

              {stepConfirm}
            </div>
          )}
          </div>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SipCallModal;
